import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';

import { firstValueFrom, take } from 'rxjs';

import { QueryClient } from '@tanstack/angular-query-experimental';
import { WidgetApiService } from 'src/app/api/services/widget-api.service';
import { widgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';
import { augmentWidgetFields } from 'src/app/api/normalize/widget.normalize';
import { firstAvailableWidgetRow } from 'src/app/shared/utils/widget-list.utils';

import {
  createWidgetFail,
  createWidgetSuccess,
  deleteWidgets as deleteWidgetsAction,
  deleteWidgetsFail,
  deleteWidgetsSuccess,
  deleteWidgetSuccess,
  massUpdateWidgetsSuccess,
  updateWidgetSuccess,
  widgetMassOperationSuccess,
} from '../state/widget.actions';
import { Widget } from '../models/core.models';
import { WidgetOperation } from '../models/widget-operations';
import { WidgetType } from '../constants';
import {
  selectCurrentObject,
  selectSelectedPageId,
  selectSelectedPlaceholderId,
} from '../state';
import { CurrentViewWidgetsService } from './current-view-widgets.service';

/** The part of a create request a caller actually chooses. */
export interface CreateWidgetInput {
  objectId: string;
  objectClassId: number;
  chartId: number;
  chartTypeId: number;
  dataSourceObjectId: string;
  dataSourceObjectClassId: number;
}

/**
 * The owner of every widget write: HTTP, then the current view's list-cache
 * patch, then the widget-data invalidation, then the `…Success` broadcast the
 * client state still needs (edit-mode refresh in `feed.component`, spinner and
 * selection reducers). Payloads are assembled from the **current view's widget
 * list** and the store.
 *
 * A service rather than mutation hooks because several callers are root
 * services, which by api-layer.md's consumer rule may not hold hooks. It
 * injects `WidgetApiService` directly, the sanctioned service-to-service call.
 */
@Injectable({ providedIn: 'root' })
export class WidgetPersistenceService {
  private readonly store = inject(Store);
  private readonly widgetApi = inject(WidgetApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly currentViewWidgets = inject(CurrentViewWidgetsService);
  private readonly queryClient = inject(QueryClient);

  /**
   * PATCH a single widget; the payload is a widget copy with the edited fields.
   * Success is silent (the repainted widget is the feedback); failure toasts.
   */
  async updateWidget(widgetId: string, widget: unknown): Promise<void> {
    try {
      await this.updateWidgetCore(widgetId, widget);
    } catch {
      this.toastService.showError(
        this.translateService.instant('widget.failed-to-update-widget'),
      );
    }
  }

  /**
   * Merge `settingsPatch` into each widget's `settings` and PATCH the whole widget.
   *
   * Success is silent; failures aggregate into **one** toast after the fan-out
   * (never one per widget). Two rapid applies both complete, and the later
   * response wins the cache.
   */
  async applySettings(
    widgetIds: string[],
    settingsPatch: Record<string, unknown>,
  ): Promise<void> {
    let failedCount = 0;

    await Promise.all(
      widgetIds.map(async (widgetId) => {
        const widget = this.currentViewWidgets.byId(widgetId);
        if (!widget) return;

        const payload = {
          ...widget,
          settings: { ...widget.settings, ...settingsPatch },
        };

        try {
          await this.updateWidgetCore(widgetId, payload);
        } catch {
          failedCount++;
        }
      }),
    );

    if (failedCount > 0) {
      this.toastService.showError(
        this.translateService.instant('widget.failed-to-update-widgets', {
          count: failedCount,
        }),
      );
    }
  }

  /** The shared update body: HTTP → list patch → data invalidation → broadcast. */
  private async updateWidgetCore(
    widgetId: string,
    widget: unknown,
  ): Promise<void> {
    const updated = await firstValueFrom(
      this.widgetApi.updateWidget(widgetId, widget),
    );

    this.currentViewWidgets.patch(widgetId, () => augmentWidgetFields(updated));
    // * updates are only ever raised for real widgets, so no placeholder guard
    this.queryClient.invalidateQueries(widgetDataQueriesFilter(widgetId));
    this.store.dispatch(updateWidgetSuccess({ widgetId, data: updated }));
  }

  /**
   * Swap a widget's preset + visualization. The swap keeps the widget's slot:
   * the response carries the new chart but the old geometry has to be preserved,
   * and the id forced back so trackBy sees an update instead of remove+add.
   */
  async swapWidgetType(
    oldWidgetId: string,
    chartId: number,
    chartTypeId: number,
  ): Promise<void> {
    try {
      const widget = await firstValueFrom(
        this.widgetApi.swapWidgetType(oldWidgetId, chartId, chartTypeId),
      );

      this.currentViewWidgets.patch(oldWidgetId, (existing) => ({
        ...augmentWidgetFields(widget),
        id: oldWidgetId,
        x: existing.x,
        y: existing.y,
        col: existing.col,
        row: existing.row,
        cols: existing.cols,
        rows: existing.rows,
        sizeX: existing.sizeX,
        sizeY: existing.sizeY,
      }));

      if (widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER) {
        this.queryClient.invalidateQueries(
          widgetDataQueriesFilter(oldWidgetId),
        );
      }

      this.toastService.showSuccess(
        this.translateService.instant(
          'widget.widget-type-swapped-successfully',
        ),
      );
    } catch {
      this.toastService.showError(
        this.translateService.instant('widget.failed-to-swap-widget-type'),
      );
    }
  }

  /**
   * Apply one settings payload to many widgets: per-widget list patch and data
   * invalidation, one success toast with the server's count.
   */
  async massUpdateWidgets(widgetIds: string[], data: unknown): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.widgetApi.massUpdate(widgetIds, data),
      );

      for (const widgetId of widgetIds) {
        const updated = response.widgets?.find((w) => w.id === widgetId);
        if (updated) {
          this.currentViewWidgets.patch(widgetId, (existing) => ({
            ...existing,
            ...augmentWidgetFields(updated),
          }));
        }

        this.queryClient.invalidateQueries(widgetDataQueriesFilter(widgetId));
      }

      this.toastService.showSuccess(
        this.translateService.instant(
          'widget.widget-settings-updated-successfully',
          { count: response.updated_count || 0 },
        ),
      );

      this.store.dispatch(
        massUpdateWidgetsSuccess({
          widgetIds,
          data,
          countUpdated: response.updated_count || 0,
          widgets: response.widgets,
        }),
      );
    } catch (error: any) {
      this.toastService.showError(
        this.translateService.instant(
          'widget.failed-to-update-widget-settings',
          {
            error: error?.message || '',
          },
        ),
      );
    }
  }

  /**
   * Delete a single widget. Success is silent (the row leaving the view is the
   * feedback); failure toasts.
   */
  async deleteWidget(widgetId: string): Promise<void> {
    try {
      await firstValueFrom(this.widgetApi.deleteWidget(widgetId));

      this.currentViewWidgets.remove([widgetId]);
      this.store.dispatch(deleteWidgetSuccess({ widgetId }));
    } catch {
      this.toastService.showError(
        this.translateService.instant('widget.failed-to-delete-widget'),
      );
    }
  }

  /**
   * Bulk delete. The trigger action raises the `loadingWidgets` spinner; the
   * Success / Fail broadcasts lower it again (and Success drops the selection).
   */
  async deleteWidgets(widgetIds: string[]): Promise<void> {
    this.store.dispatch(deleteWidgetsAction({ widgetIds }));

    try {
      await firstValueFrom(this.widgetApi.deleteWidgets(widgetIds));

      this.currentViewWidgets.remove(widgetIds);
      this.store.dispatch(deleteWidgetsSuccess({ widgetIds }));
    } catch (error) {
      this.toastService.showError(
        this.translateService.instant('widget.failed-to-delete-widgets'),
      );
      this.store.dispatch(deleteWidgetsFail({ error }));
    }
  }

  /**
   * Copy/move widgets between views. The page id is read **before** the request,
   * so a same-page copy is judged against the page the user was on when the
   * operation started. Only *moved* widgets leave the current view; a copy back
   * into the current view refetches the list - the copies are new widgets, so
   * the response is the only source for them.
   */
  async massOperation(operations: WidgetOperation[]): Promise<void> {
    const currentPageId = await firstValueFrom(
      this.store.select(selectSelectedPageId).pipe(take(1)),
    );
    const currentObject = await firstValueFrom(
      this.store.select(selectCurrentObject).pipe(take(1)),
    );

    try {
      await firstValueFrom(this.widgetApi.massOperation(operations));
    } catch (error) {
      // * 400s only - 500s are handled on the general level
      const isCopy = operations[0]?.action === 'copy';

      this.toastService.showError(
        this.translateService.instant(
          isCopy
            ? 'widget.failed-to-copy-widgets'
            : 'widget.failed-to-move-widgets',
        ),
      );

      console.error('Mass operation failed:', error, 'Operations:', operations);
      return;
    }

    const isCopy = operations[0].action === 'copy';
    const destination = this.translateService.instant(
      'widget.destination.' + operations[0].scenario,
    );

    this.toastService.showSuccess(
      this.translateService.instant(
        isCopy
          ? 'widget.widgets-copied-to-destination'
          : 'widget.widgets-moved-to-destination',
        { count: operations.length, destination },
      ),
    );

    this.currentViewWidgets.remove(
      operations
        .filter((operation) => operation.action === 'move')
        .map((operation) => operation.widget_id),
    );

    const copiedToSameView = operations.some((op) => {
      if (op.action !== 'copy') return false;

      if (op.scenario === 'same-dashboard') {
        return (
          currentPageId &&
          op.target_object_ids[0] === currentObject?.id &&
          op.target_page_id === currentPageId
        );
      }

      if (op.scenario === 'same-feed') {
        return op.target_object_ids[0] === currentObject?.id;
      }

      // * other scenarios -> copying to different Objects, no reload needed
      return false;
    });

    if (copiedToSameView) {
      this.currentViewWidgets.invalidate();
    }

    this.store.dispatch(widgetMassOperationSuccess({ operations }));
  }

  /**
   * Create a widget in the current view. Row and placeholder geometry come from
   * the list the view is rendering:
   * - `row` - the first row no real widget occupies
   * - `placeholderWidgetId` - the placeholder the user clicked, unless it is the
   *   server-side **fake** one (no DB row to reuse)
   * - `col` / `sizeX` / `sizeY` - that placeholder's geometry, so the new widget
   *   lands exactly where the tile was
   * - `page_id` - the selected dashboard page, null for a feed
   *
   * The **list is invalidated rather than patched**: the backend appends a freshly
   * positioned fake placeholder to every feed's `get_object_widgets` response, and
   * reproducing that by hand is not worth it.
   */
  async createWidget(input: CreateWidgetInput): Promise<void> {
    const placeholderId = await firstValueFrom(
      this.store.select(selectSelectedPlaceholderId).pipe(take(1)),
    );
    const pageId = await firstValueFrom(
      this.store.select(selectSelectedPageId).pipe(take(1)),
    );

    const widgets = this.currentViewWidgets.snapshot();
    const placeholder = placeholderId
      ? widgets.find((widget) => widget.id === placeholderId)
      : undefined;

    try {
      const widget = await firstValueFrom(
        this.widgetApi.createWidget({
          chartId: input.chartId,
          chartTypeId: input.chartTypeId,
          objectId: input.objectId,
          objectClassId: input.objectClassId,
          dataSourceObjectId: input.dataSourceObjectId,
          dataSourceObjectClassId: input.dataSourceObjectClassId,
          row: firstAvailableWidgetRow(widgets),
          placeholderWidgetId: placeholder?.is_fake ? null : placeholderId,
          col: placeholder?.col,
          sizeX: placeholder?.sizeX,
          sizeY: placeholder?.sizeY,
          pageId,
        }),
      );

      this.toastService.showSuccess(
        this.translateService.instant('widget.widget-created-successfully'),
      );

      this.currentViewWidgets.invalidate();

      if (widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER) {
        this.queryClient.invalidateQueries(widgetDataQueriesFilter(widget.id));
      }

      this.store.dispatch(
        createWidgetSuccess({ widget, objectClassId: input.objectClassId }),
      );
    } catch (error) {
      // ! a failed create is silent by design - no toast
      this.store.dispatch(createWidgetFail({ error }));
    }
  }

  /**
   * Persist the whole view's layout. Called only by
   * `WidgetLayoutStagingService`, which owns the debounce, the search-string
   * guard and the pending-geometry overlay.
   */
  updateWidgetsOrder(widgets: Widget[]): Promise<unknown> {
    return firstValueFrom(this.widgetApi.updateWidgetsOrder(widgets));
  }
}
